import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { saveAs } from "file-saver";
import * as JSZip from "jszip";
import { ToastrService } from "ngx-toastr";
import { take } from "rxjs/operators";
import { DownloadService } from "../../../../shared/services/download.service";
import { LoadingService } from "../../../../shared/services/loading.service";
import { TranslateService } from "../../../../shared/services/translate.service";
import { environment } from "src/environments/environment";
import { DigitalMemoryLogService } from "../../services/digital-memory-log.service";
import { PreviewTempDataService } from "../../services/preview-temp-data.service";
import AppShareHelper from "../../../../shared/utils/helper";
import { DownloadProgress } from "./components/download-progress/download-progress.component";
import { ErrorPopupComponent } from "./components/error-popup/error-popup.component";
import { PreviewPopupComponent } from "./components/preview-popup/preview-popup.component";
import { random } from "underscore";
import * as streamSaver from "streamsaver";

// Interface for Clipboard API
interface Clipboard {
  writeText(text: string): Promise<void>;
  readText(): Promise<string>;
}

/** Fixed height (px) of one virtual scroll row - enables CDK to compute scroll size */
const VIRTUAL_SCROLL_ROW_HEIGHT = 420;
/** Number of columns in grid layout */
const GRID_COLUMNS = 3;

@Component({
  selector: "app-file-detail",
  templateUrl: "./file-detail.component.html",
  styleUrls: ["./file-detail.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileDetailComponent implements OnInit, OnDestroy {
  private readonly defaultVideoPoster = "assets/images/no-image.svg";

  brand: any = {
    brandColor: "#FFA300",
    brandFont: "Arial, sans-serif",
    fontColor: "#FFA300",
    displayBrandLogo: false,
    brandLogo: null,
    displayFiles: false,
    displayVideos: false,
    displayPhotos: false,
  };
  showErrorPopup = false;
  subSection = null;
  loading = true;
  error = false;
  selectedFiles: Set<string | number> = new Set();
  selectedCount = 0;
  type: "file" | "photo" | "video" | "media" = "file";
  isFromPreview = false; // Flag to check if user came from preview page
  previewType: string = ""; // Store preview type (edit/create) to detect if opened from travel-kit-modify

  /** Share button is disabled when preview is opened from create/edit screen */
  get isShareDisabled(): boolean {
    return this.isFromPreview && (this.previewType === "edit" || this.previewType === "create");
  }
  invalidMediaUrls: Set<string> = new Set(); // Track media items with invalid/broken URLs

  // Download progress properties
  downloadProgress: DownloadProgress = {
    current: 0,
    total: 0,
    percentage: 0,
    currentFile: "",
    status: "idle",
    message: "",
  };
  showDownloadProgress = false;
  downloadCancelled = false;
  isNoFiles = false;

  isTranslated = false;
  translating = false;
  private originalSubSection: any = null;

  // AbortController for cancelling downloads
  private downloadAbortController: AbortController | null = null;

  /** Expose for template - CDK virtual scroll requires fixed item height */
  readonly itemSize = VIRTUAL_SCROLL_ROW_HEIGHT;

  /** Object URLs created by this component - revoke on destroy to prevent memory leaks */
  private objectUrlsToRevoke: Set<string> = new Set();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private digitalMemoryLogService: DigitalMemoryLogService,
    private downloadService: DownloadService,
    private loadingService: LoadingService,
    private toastr: ToastrService,
    private modalService: NgbModal,
    private previewTempDataService: PreviewTempDataService,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    // Check if user came from preview page
    this.checkNavigationHistory();

    // Get parameters from route
    this.route.params.subscribe(params => {
      const previewId = params["previewId"];
      const subSectionId = params["subSectionId"];

      // Get type from query params
      this.route.queryParams.subscribe(queryParams => {
        this.type = queryParams["type"] || "file";

        // Check if we're in preview mode from create/edit
        const queryType = queryParams["type"] || "";
        if (queryType === "edit" || queryType === "create") {
          this.isFromPreview = true;
          this.previewType = queryType;
        }
      });

      if (previewId && subSectionId) {
        // Route: /preview/:id/file-detail/:subSectionId
        (async () => {
          await this.loadSubSectionData(previewId, subSectionId);
        })();
      } else if (previewId) {
        (async () => {
          await this.loadSubSectionDataWithSubSectionIdOnly(previewId);
        })();
      } else {
        // No previewId or subSectionId provided
        this.showContentUnavailableError();
      }
    });
  }

  private checkNavigationHistory(): void {
    // Method 1: Check if we have stored preview info (most reliable)
    const storedPreviewInfo = sessionStorage.getItem("previewNavigationInfo");
    if (storedPreviewInfo) {
      try {
        const info = JSON.parse(storedPreviewInfo);
        const currentTime = Date.now();
        // Check if the stored info is recent (within 5 minutes)
        if (currentTime - info.timestamp < 5 * 60 * 1000) {
          this.isFromPreview = true;
          // Store preview type for later use
          if (info.previewType) {
            // Store preview type to detect if opened from travel-kit-modify
            this.previewType = info.previewType;
          }
          return;
        } else {
          // Clean up old data
          sessionStorage.removeItem("previewNavigationInfo");
        }
      } catch (error) {
        sessionStorage.removeItem("previewNavigationInfo");
      }
    }

    // Method 2: Check browser history (fallback)
    if (window.history.length > 1) {
      // Try to get previous page from sessionStorage
      const previousUrl = sessionStorage.getItem("previousUrl");
      if (previousUrl && previousUrl.includes("/preview/")) {
        this.isFromPreview = true;
        return;
      }
    }

    // Method 3: Check if current URL is a direct preview navigation
    // Only set to true if we're on a route that was specifically created for preview navigation
    const currentUrl = this.router.url;
    if (currentUrl.includes("/preview/") && currentUrl.includes("/file-detail/")) {
      // This is a preview-to-file-detail route, but we need to verify it came from preview
      // We'll rely on the sessionStorage check above instead
      this.isFromPreview = false;
    }
  }

  async loadSubSectionData(previewId: string, subSectionId: string): Promise<void> {
    this.loading = true;
    this.error = false;

    // Check if we should load from storage
    // Priority: 1) Check if isFromPreview and has data in storage, 2) Check query params, 3) Check if storage has data
    const queryType = this.route.snapshot.queryParams["type"] || "";
    const previewType = this.previewType || "";
    const hasStorageData = this.previewTempDataService.hasTempPreviewData(previewId);

    const shouldLoadFromStorage =
      (this.isFromPreview && hasStorageData) ||
      queryType === "edit" ||
      queryType === "create" ||
      previewType === "edit" ||
      previewType === "create" ||
      hasStorageData;

    if (shouldLoadFromStorage) {
      // Load from storage
      await this.loadSubSectionDataFromStorage(previewId, subSectionId);
    } else {
      // Load from API
      await this.loadSubSectionDataFromApi(previewId, subSectionId);
    }
  }

  private async loadSubSectionDataFromStorage(previewId: string, subSectionId: string): Promise<void> {
    try {
      const tempData = this.previewTempDataService.getTempPreviewData(previewId);

      if (tempData) {
        console.log("Loading subsection from storage:", { previewId, subSectionId, type: this.type });

        // Check if content type is enabled
        if (!this.isContentTypeEnabled(tempData)) {
          console.error("Content type not enabled:", { type: this.type, tempData });
          this.showContentUnavailableError();
          return;
        }

        // Get the correct sections based on type
        this.brand = tempData;
        let sections;
        switch (this.type) {
          case "photo":
            sections = tempData.photoSections;
            break;
          case "video":
            sections = tempData.videoSections;
            break;
          case "media":
            sections = tempData.mediaSections;
            break;
          default:
            sections = tempData.fileSections;
        }

        console.log("Sections found:", {
          sectionsLength: sections.length,
          type: this.type,
          sections: sections.map((s: any, idx: number) => ({
            index: idx,
            name: s.name,
            subSectionsCount: s.subSections.length || 0,
            subSections: s.subSections.map((sub: any, subIdx: number) => ({
              index: subIdx,
              id: sub.id,
              title: sub.title,
              mediaCount: sub.media.length || 0,
            })),
          })),
        });

        // Find the specific subSection by ID or by index
        if (sections && Array.isArray(sections)) {
          let targetSubSection = null;

          // First, try to find by ID
          for (let i = 0; i < sections.length; i++) {
            const section = sections[i];
            if (section.subSections && Array.isArray(section.subSections)) {
              targetSubSection = section.subSections.find((subSection: any) => {
                // Check if subsection has id and matches
                if (subSection.id) {
                  return subSection.id.toString() === subSectionId;
                }
                return false;
              });

              if (targetSubSection) {
                console.log("Found subsection by ID:", { sectionIndex: i, targetSubSection });
                break;
              }
            }
          }

          // If not found by ID, try to find by index (for new subsections without id)
          if (!targetSubSection) {
            // Get navigation info to find by index
            const storedPreviewInfo = sessionStorage.getItem("previewNavigationInfo");
            if (storedPreviewInfo) {
              try {
                const info = JSON.parse(storedPreviewInfo);
                console.log("Navigation info:", info);
                if (info.sectionIndex !== undefined && info.subIndex !== undefined) {
                  const sectionIndex = info.sectionIndex;
                  const subIndex = info.subIndex;

                  console.log("Trying to find by index:", {
                    sectionIndex,
                    subIndex,
                    sectionsLength: sections.length,
                    sectionExists: !!sections[sectionIndex],
                    sectionName: sections[sectionIndex].name,
                    subSectionsExists: !!sections[sectionIndex].subSections,
                    subSectionsLength: sections[sectionIndex].subSections.length,
                  });

                  if (
                    sections[sectionIndex] &&
                    sections[sectionIndex].subSections &&
                    sections[sectionIndex].subSections[subIndex]
                  ) {
                    targetSubSection = sections[sectionIndex].subSections[subIndex];
                    console.log("Found subsection by index:", {
                      targetSubSection,
                      title: targetSubSection.title,
                      mediaCount: targetSubSection.media.length || 0,
                    });
                  } else {
                    console.error("Subsection not found at index:", {
                      sectionIndex,
                      subIndex,
                      hasSection: !!sections[sectionIndex],
                      hasSubSections: !!sections[sectionIndex].subSections,
                      subSectionsLength: sections[sectionIndex].subSections.length,
                      availableIndices: sections.map((s: any, idx: number) => ({
                        sectionIndex: idx,
                        subSectionsCount: s.subSections.length || 0,
                      })),
                    });
                  }
                }
              } catch (error) {
                console.error("Error parsing navigation info:", error);
              }
            }

            // If still not found, try to match by temporary ID format (temp-{sectionIndex}-{subIndex})
            if (!targetSubSection && subSectionId.startsWith("temp-")) {
              const parts = subSectionId.split("-");
              if (parts.length === 3) {
                const sectionIndex = parseInt(parts[1], 10);
                const subIndex = parseInt(parts[2], 10);
                console.log("Trying to find by temp ID:", { sectionIndex, subIndex });
                if (
                  !isNaN(sectionIndex) &&
                  !isNaN(subIndex) &&
                  sections[sectionIndex] &&
                  sections[sectionIndex].subSections &&
                  sections[sectionIndex].subSections[subIndex]
                ) {
                  targetSubSection = sections[sectionIndex].subSections[subIndex];
                  console.log("Found subsection by temp ID:", { targetSubSection });
                }
              }
            }
          }

          if (targetSubSection) {
            console.log("Subsection found:", {
              targetSubSection,
              hasTitle: !!targetSubSection.title,
              hasMedia: !!targetSubSection.media,
              mediaLength: targetSubSection.media.length || 0,
              media: targetSubSection.media,
            });

            this.subSection = targetSubSection;

            // Validate all media URLs before showing UI to ensure only valid URLs are displayed
            await this.validateMediaUrls();

            // Check valid media count (after filtering invalid URLs)
            const validMediaCount = this.validMedia.length;
            if (!targetSubSection.media || validMediaCount === 0) {
              this.isNoFiles = true;
            }

            this.loading = false;
            this.cdr.markForCheck();

            // Cập nhật brand settings từ preview data
            this.updateBrandSettings(tempData);
            console.log("Subsection loaded successfully:", {
              subSection: this.subSection,
              title: this.subSection.title,
              mediaCount: this.subSection.media.length || 0,
            });
          } else {
            // SubSection not found - log detailed info for debugging
            console.error("SubSection not found in storage:", {
              subSectionId,
              type: this.type,
              sectionsLength: sections.length,
              sections: sections.map((s: any, idx: number) => ({
                index: idx,
                name: s.name,
                subSectionsCount: s.subSections.length || 0,
                subSections: s.subSections.map((sub: any, subIdx: number) => ({
                  index: subIdx,
                  id: sub.id,
                  title: sub.title,
                })),
              })),
            });
            this.showContentUnavailableError();
          }
        } else {
          // No sections found for this type
          console.error("No sections found for type:", { type: this.type, sections });
          this.showContentUnavailableError();
        }
      } else {
        // No data in storage, fallback to API
        console.error("No temp data found in storage for previewId:", previewId);
        await this.loadSubSectionDataFromApi(previewId, subSectionId);
      }
    } catch (error) {
      console.error("Error loading data from storage:", error);
      // Fallback to API on error
      await this.loadSubSectionDataFromApi(previewId, subSectionId);
    }
  }

  private async loadSubSectionDataFromApi(previewId: string, subSectionId: string): Promise<void> {
    try {
      // Call API to get preview data
      const previewData: any = await this.digitalMemoryLogService.getPreviewById(previewId).pipe(take(1)).toPromise();

      // Check if content type is enabled
      if (!this.isContentTypeEnabled(previewData)) {
        this.showContentUnavailableError();
        return;
      }

      // Get the correct sections based on type
      this.brand = previewData;
      let sections;
      switch (this.type) {
        case "photo":
          sections = previewData.photoSections;
          break;
        case "video":
          sections = previewData.videoSections;
          break;
        case "media":
          sections = previewData.mediaSections;
          break;
        default:
          sections = previewData.fileSections;
      }

      // Find the specific subSection by ID in the correct sections array
      if (sections && Array.isArray(sections)) {
        let targetSubSection = null;
        // Search through all sections and their subSections
        for (const section of sections) {
          if (section.subSections && Array.isArray(section.subSections)) {
            targetSubSection = section.subSections.find((subSection: any) => {
              const matches = subSection.id.toString() === subSectionId;
              return matches;
            });

            if (targetSubSection) {
              break;
            }
          }
        }

        if (targetSubSection) {
          this.subSection = targetSubSection;

          // Validate all media URLs before showing UI to ensure only valid URLs are displayed
          await this.validateMediaUrls();

          // Check valid media count (after filtering invalid URLs)
          const validMediaCount = this.validMedia.length;
          if (!targetSubSection.media || validMediaCount === 0) {
            this.isNoFiles = true;
          }

          this.loading = false;
          this.cdr.markForCheck();

          // Cập nhật brand settings từ preview data
          this.updateBrandSettings(previewData);
        } else {
          // SubSection not found
          this.showContentUnavailableError();
        }
      } else {
        // No sections found for this type
        this.showContentUnavailableError();
      }
    } catch (error) {
      this.showContentUnavailableError();
    }
  }

  // Thêm method để cập nhật brand settings
  private updateBrandSettings(data: any): void {
    this.brand = {
      brandColor: data.brandColor || "#FFA300",
      brandFont: data.brandFont || "Arial, sans-serif",
      fontColor: data.fontColor || "#FFA300", // Thêm fontColor
      displayBrandLogo: data.displayBrandLogo || false,
      brandLogo: data.brandLogo || null,
    };
    // Cập nhật CSS variables
    this.updateBrandColor(data.brandColor);
    this.updateBrandFont(data.brandFont);
  }

  // Cập nhật method updateBrandFont
  private updateBrandFont(brandFont: string): void {
    if (brandFont) {
      // Map font names to CSS variables
      const fontMapping: { [key: string]: string } = {
        Arial: "var(--font-arial)",
        Helvetica: "var(--font-helvetica)",
        "Times New Roman": "var(--font-times)",
        Georgia: "var(--font-georgia)",
        Verdana: "var(--font-verdana)",
        "Courier New": "var(--font-courier)",
        "Lucida Console": "var(--font-lucida)",
        "DejaVu Sans": "var(--font-dejavu-sans)",
        "DejaVu Serif": "var(--font-dejavu-serif)",
        Courier: "var(--font-courier-old)",
      };

      // Get the CSS variable for the font, or use the font name directly
      const fontValue = fontMapping[brandFont] || `'${brandFont}', sans-serif`;

      // Set CSS variable for brand font
      document.documentElement.style.setProperty("--brand-font", fontValue);

      // Also set on the component element itself
      const componentElement = document.querySelector("app-file-detail");
      if (componentElement) {
        (componentElement as HTMLElement).style.setProperty("--brand-font", fontValue);
      }
    }
  }
  // Cập nhật method updateBrandColor
  private updateBrandColor(brandColor: string): void {
    if (brandColor) {
      // Convert hex to RGB for rgba usage
      const hex = brandColor.replace("#", "");
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);

      // Set CSS variables on both document and component element
      document.documentElement.style.setProperty("--brand-color", brandColor);
      document.documentElement.style.setProperty("--brand-color-rgb", `${r}, ${g}, ${b}`);

      // Also set on the component element itself
      const componentElement = document.querySelector("app-file-detail");
      if (componentElement) {
        (componentElement as HTMLElement).style.setProperty("--brand-color", brandColor);
        (componentElement as HTMLElement).style.setProperty("--brand-color-rgb", `${r}, ${g}, ${b}`);
      }
    }
  }

  // Check if content type is enabled based on current type
  private isContentTypeEnabled(previewData: any): boolean {
    switch (this.type) {
      case "file":
        return previewData.displayFiles === true;
      case "photo":
        return previewData.displayPhotos === true;
      case "video":
        return previewData.displayVideos === true;
      case "media":
        return previewData.displayMedias === true;
      default:
        return false;
    }
  }

  // Show content unavailable error popup
  private showContentUnavailableError(): void {
    this.loading = false;
    this.error = false;

    const modalRef = this.modalService.open(ErrorPopupComponent, {
      size: "sm",
      backdrop: "static",
      keyboard: false,
      centered: true,
      windowClass: "error-popup-modal",
    });

    modalRef.componentInstance.isFromPreview = this.isFromPreview;

    modalRef.result
      .then(result => {
        if (result === "back-to-main") {
          // Navigate back to main page
          this.router.navigate(["/"]);
        } else if (result === "go-back") {
          // Go back to previous page
          window.history.back();
        }
      })
      .catch(() => {
        // Modal dismissed
        if (this.isFromPreview) {
          window.history.back();
        }
      });
  }

  async loadSubSectionDataWithSubSectionIdOnly(subSectionId: string): Promise<void> {
    this.loading = true;
    this.error = false;

    // Check if we should load from storage
    const queryType = this.route.snapshot.queryParams["type"] || "";
    const shouldLoadFromStorage = this.isFromPreview || queryType === "edit" || queryType === "create";

    // Option 1: Get from localStorage if available
    const storedPreviewId = localStorage.getItem("currentPreviewId");
    if (storedPreviewId) {
      await this.loadSubSectionData(storedPreviewId, subSectionId);
    } else if (shouldLoadFromStorage) {
      // Try to get previewId from sessionStorage navigation info
      const storedPreviewInfo = sessionStorage.getItem("previewNavigationInfo");
      if (storedPreviewInfo) {
        try {
          const info = JSON.parse(storedPreviewInfo);
          if (info.previewId) {
            await this.loadSubSectionData(info.previewId, subSectionId);
            return;
          }
        } catch (error) {
          console.error("Error parsing preview navigation info:", error);
        }
      }
      // No previewId found, show error
      this.showContentUnavailableError();
    } else {
      // No previewId found, show error
      this.showContentUnavailableError();
    }
  }

  retryLoad(): void {
    this.route.params.subscribe(params => {
      const previewId = params["previewId"] || params["shipId"] || params["id"];
      const subSectionId = params["subSectionId"];
      if (previewId && subSectionId) {
        (async () => {
          await this.loadSubSectionData(previewId, subSectionId);
        })();
      } else if (previewId) {
        (async () => {
          await this.loadSubSectionDataWithSubSectionIdOnly(previewId);
        })();
      } else {
        this.showContentUnavailableError();
      }
    });
  }

  onBackClick(): void {
    // Navigate back to previous page
    window.history.back();
  }

  onShareClick(): void {
    // Check if opened from travel-kit-modify (previewType is "edit" or "create")
    if (this.isFromPreview && (this.previewType === "edit" || this.previewType === "create")) {
      this.toastr.error("You can not share content while in preview mode.", "Error");
      return;
    }

    try {
      const currentUrl = window.location.href;
      // Copy URL to clipboard
      const nav = navigator as Navigator & { clipboard?: Clipboard };
      if (nav.clipboard) {
        nav.clipboard
          .writeText(currentUrl)
          .then(() => {
            this.toastr.success("URL copied to clipboard", "Success");
          })
          .catch(error => {
            this.fallbackCopyUrl(currentUrl);
          });
      } else {
        // Fallback for older browsers
        this.fallbackCopyUrl(currentUrl);
      }
    } catch (error) {
      this.toastr.error("Failed to copy URL", "Error");
    }
  }

  private fallbackCopyUrl(url: string): void {
    // Fallback method for older browsers
    const textArea = document.createElement("textarea");
    textArea.value = url;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      const successful = document.execCommand("copy");
      if (successful) {
        this.toastr.success("URL copied to clipboard", "Success");
      } else {
        this.toastr.error("Failed to copy URL", "Error");
      }
    } catch (error) {
      this.toastr.error("Failed to copy URL", "Error");
    }
    document.body.removeChild(textArea);
  }

  onDownloadAllClick(): void {
    if (this.type === "video" || this.type === "media") {
      return;
    }
    if (this.subSection && this.subSection.media) {
      const files = this.validMedia; // Only download valid media files
      if (files.length > 1) {
        // Download as zip if multiple files
        this.downloadWithProgress(files, true, true); // true = isZip, true = isDownloadAll
      } else if (files.length === 1) {
        // Download single file directly
        this.downloadSingleFile(files[0]);
      }
    }
  }

  onCancelDownload(): void {
    // When not actively downloading: just close popup, no cancel message
    if (this.downloadProgress.status !== "downloading" && this.downloadProgress.status !== "preparing") {
      this.closeDownloadProgress();
      return;
    }

    // Check if there's no active download
    if (!this.isDownloadActive()) {
      this.closeDownloadProgress();
      return;
    }

    this.downloadCancelled = true;

    // Abort all ongoing fetch requests
    if (this.downloadAbortController) {
      this.downloadAbortController.abort();
      this.downloadAbortController = null;
    }

    this.showDownloadProgress = false;
    this.downloadProgress.status = "idle";
    this.cdr.markForCheck();
    this.toastr.info("Download cancelled", "Info");
    this.loadingService.complete(); // Complete loading service
  }

  private closeDownloadProgress(): void {
    this.showDownloadProgress = false;
    this.downloadProgress.status = "idle";
    this.loadingService.complete();
    this.cdr.markForCheck();
  }

  private isDownloadActive(): boolean {
    return (
      this.downloadProgress.status === "downloading" &&
      this.showDownloadProgress &&
      this.downloadAbortController !== null
    );
  }

  private async downloadAsZip(files: any[], isDownloadAll: boolean = true): Promise<void> {
    if (this.type === "video" || this.type === "media") {
      return;
    }

    // Nếu là photo type, chia thành nhiều zip nhỏ (mỗi zip 100 ảnh)
    if (this.type === "photo") {
      await this.downloadPhotosAsMultipleZips(files, isDownloadAll);
      return;
    }

    // Logic cũ cho file type
    this.initializeDownloadProgress(files.length);
    this.downloadAbortController = new AbortController();
    const zip = new JSZip();
    let completed = 0;

    setTimeout(() => {
      this.updateDownloadProgress(0, files.length, "Downloading...", random(1, Math.min(5, 100 / files.length - 2)));
    }, 0);

    try {
      for (let index = 0; index < files.length; index++) {
        const file = files[index];

        // chọn method
        const fetchMethod = this.getDownloadBlobPromise(file, this.downloadAbortController.signal);

        const blob = await fetchMethod;
        if (this.downloadCancelled || this.downloadAbortController.signal.aborted) {
          throw new Error("Download cancelled");
        }

        const filename = file.fileName || file.url.split("/").pop() || `file_${index + 1}`;
        zip.file(filename, blob);

        completed++;
        this.updateDownloadProgress(completed, files.length, `Downloaded ${filename}`);
      }

      // sau khi tải hết → tạo zip

      this.updateDownloadProgress(files.length, files.length, "Creating ZIP file...");
      const content = await zip.generateAsync({ type: "blob", streamFiles: true, compression: "STORE" });

      if (this.downloadCancelled || this.downloadAbortController.signal.aborted) {
        throw new Error("Download cancelled");
      }

      const zipName = `${this.subSection.title || "files"}.zip`;
      saveAs(content, zipName);
      this.completeDownload("ZIP file created successfully");
      this.toastr.success(`Started downloading: ${zipName}`, "Success");

      this.loadingService.complete();
    } catch (error) {
      if (error.message === "Download cancelled" || error.name === "AbortError") {
        this.cancelDownload();
      } else {
        this.errorDownload("Error downloading files. Please try again.");
        this.toastr.error("Error downloading files. Please try again.", "Error");
      }
      this.loadingService.complete();
    }
  }

  private async downloadPhotosAsMultipleZips(files: any[], isDownloadAll: boolean = true): Promise<void> {
    const IMAGES_PER_ZIP = 100;
    const totalZips = Math.ceil(files.length / IMAGES_PER_ZIP);

    this.initializeDownloadProgress(files.length);
    this.downloadAbortController = new AbortController();

    try {
      // Chia files thành các chunk 100 ảnh
      const chunks: any[][] = [];
      for (let i = 0; i < files.length; i += IMAGES_PER_ZIP) {
        chunks.push(files.slice(i, i + IMAGES_PER_ZIP));
      }

      console.log(`Starting download: ${files.length} photos, ${chunks.length} ZIP files`);
      chunks.forEach((chunk, idx) => {
        console.log(`Chunk ${idx + 1}: ${chunk.length} photos`);
      });

      let totalCompletedImages = 0;

      // Xử lý từng zip
      for (let zipIndex = 0; zipIndex < chunks.length; zipIndex++) {
        const chunk = chunks[zipIndex];
        const zip = new JSZip();
        let completedInChunk = 0;

        // Cập nhật progress: đang xử lý zip nào
        const initialProgressPercent =
          totalCompletedImages > 0 ? Math.round((totalCompletedImages / files.length) * 100) : 0;
        const zipProgressText =
          totalZips > 1
            ? `Creating ZIP ${zipIndex + 1}/${totalZips} (0/${chunk.length} images)...`
            : `Creating ZIP (0/${chunk.length} images)...`;
        this.updateDownloadProgress(totalCompletedImages, files.length, zipProgressText, initialProgressPercent);

        // Tải từng ảnh trong chunk
        // Track filenames để tránh duplicate
        const usedFilenames = new Set<string>();
        let failedFiles = 0;

        for (let index = 0; index < chunk.length; index++) {
          const file = chunk[index];

          if (this.downloadCancelled || this.downloadAbortController.signal.aborted) {
            throw new Error("Download cancelled");
          }

          try {
            const blob = await this.downloadService.fetchImageAsBlob(file.url, this.downloadAbortController.signal);

            if (this.downloadCancelled || this.downloadAbortController.signal.aborted) {
              throw new Error("Download cancelled");
            }

            // Tạo filename unique để tránh overwrite
            let baseFilename = file.fileName || file.url.split("/").pop() || `image_${totalCompletedImages + 1}`;

            // Đảm bảo filename là unique trong zip này
            let filename = baseFilename;
            let counter = 1;
            while (usedFilenames.has(filename)) {
              // Nếu filename đã tồn tại, thêm số vào trước extension
              const ext = baseFilename.includes(".") ? baseFilename.substring(baseFilename.lastIndexOf(".")) : "";
              const nameWithoutExt = baseFilename.includes(".")
                ? baseFilename.substring(0, baseFilename.lastIndexOf("."))
                : baseFilename;
              filename = `${nameWithoutExt}_${counter}${ext}`;
              counter++;
            }
            usedFilenames.add(filename);

            zip.file(filename, blob);

            completedInChunk++;
            totalCompletedImages++;

            // Cập nhật progress chi tiết - tính theo số images đã tải
            const progressPercent = Math.round((totalCompletedImages / files.length) * 100);
            this.updateDownloadProgress(
              totalCompletedImages,
              files.length,
              `Downloading images for ZIP ${zipIndex + 1}/${totalZips} (${completedInChunk}/${chunk.length} images)...`,
              progressPercent
            );
          } catch (error) {
            // Log lỗi nhưng tiếp tục với file tiếp theo
            console.error(`Failed to download file ${index + 1} in chunk ${zipIndex + 1}:`, file.url, error);
            failedFiles++;
            // Vẫn tăng counter để đảm bảo progress chính xác
            totalCompletedImages++;
          }
        }

        if (failedFiles > 0) {
          console.warn(`Chunk ${zipIndex + 1}: ${failedFiles} files failed to download out of ${chunk.length}`);
        }

        // Tạo zip file
        if (this.downloadCancelled || this.downloadAbortController.signal.aborted) {
          throw new Error("Download cancelled");
        }

        // Cập nhật progress khi đang tạo ZIP file (không tăng số images)
        const creatingZipProgressPercent = Math.round((totalCompletedImages / files.length) * 100);
        const creatingZipText =
          totalZips > 1 ? `Creating ZIP file ${zipIndex + 1}/${totalZips}...` : `Creating ZIP file...`;
        this.updateDownloadProgress(totalCompletedImages, files.length, creatingZipText, creatingZipProgressPercent);

        const content = await zip.generateAsync({ type: "blob", streamFiles: true, compression: "STORE" });

        if (this.downloadCancelled || this.downloadAbortController.signal.aborted) {
          throw new Error("Download cancelled");
        }

        // Verify số lượng files trong zip
        const filesInZip = Object.keys(zip.files).filter(key => !zip.files[key].dir).length;
        console.log(
          `ZIP ${zipIndex + 1}/${totalZips}: Expected ${chunk.length} files, Actual ${filesInZip} files in ZIP`
        );

        if (filesInZip !== chunk.length) {
          console.warn(`Warning: ZIP ${zipIndex + 1} has ${filesInZip} files but expected ${chunk.length} files`);
        }

        // Tải zip file - chỉ thêm part<number> khi download all và có nhiều hơn 1 ZIP file
        const partSuffix = isDownloadAll && totalZips > 1 ? `_part${zipIndex + 1}` : "";
        const zipName = `${this.subSection.title || "photos"}${partSuffix}.zip`;
        saveAs(content, zipName);

        // Cập nhật progress sau khi tải xong zip
        // Progress vẫn tính theo số images, không phải số ZIP
        const zipCompletedProgressPercent = Math.round((totalCompletedImages / files.length) * 100);
        this.updateDownloadProgress(
          totalCompletedImages,
          files.length,
          `Downloaded ZIP ${zipIndex + 1}/${totalZips} (${totalCompletedImages}/${files.length} images): ${zipName}`,
          zipCompletedProgressPercent
        );

        // Delay nhỏ giữa các lần tải zip để tránh browser block
        if (zipIndex < chunks.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      const completeMessage =
        totalZips > 1
          ? `Successfully downloaded ${totalZips} ZIP files (${files.length} images)`
          : `Successfully downloaded ZIP file (${files.length} images)`;
      const toastMessage =
        totalZips > 1 ? `Successfully downloaded ${totalZips} ZIP files` : `Successfully downloaded ZIP file`;
      this.completeDownload(completeMessage);
      this.toastr.success(toastMessage, "Success");
      this.loadingService.complete();
    } catch (error) {
      if (error.message === "Download cancelled" || error.name === "AbortError") {
        this.cancelDownload();
      } else {
        this.errorDownload("Error downloading images. Please try again.");
        this.toastr.error("Error downloading images. Please try again.", "Error");
      }
      this.loadingService.complete();
    }
  }

  onPreviewClick(file: any): void {
    try {
      if (this.type === "file") {
        // For file type, validate URL first
        if (!this.isUrlValid(file.url)) {
          this.toastr.error("File URL is invalid or not accessible", "Error");
          this.markUrlAsInvalid(file.url);
          return;
        }

        // For file type, open in new tab
        if (file.url && this.canOpenFile(file)) {
          // Try to open file, and validate if it fails
          const newWindow = window.open(file.url, "_blank");
          if (!newWindow) {
            // If window.open fails, it might be blocked or file doesn't exist
            // Validate the URL
            this.validateUrl(file.url).catch(() => {
              this.markUrlAsInvalid(file.url);
            });
          }
        } else {
          this.toastr.error("File cannot be opened in browser", "Error");
        }
      } else if (this.type === "photo" || this.type === "video") {
        this.openPreviewModal(file, this.type, this.validMedia);
      } else if (this.type === "media") {
        if (this.isMediaPhoto(file) || this.isMediaVideo(file)) {
          this.openPreviewModal(file, "media", this.validMedia);
        } else if (file.url && this.canOpenFile(file)) {
          window.open(file.url, "_blank");
        } else {
          this.toastr.info("Preview not available for this file type", "Info");
        }
      } else {
        this.toastr.info("Preview not available for this file type", "Info");
      }
    } catch (error) {
      this.toastr.error("Error opening preview. Please try again.", "Error");
    }
  }

  private openPreviewModal(file: any, previewType: "photo" | "video" | "media", files: any[]): void {
    const validFiles = Array.isArray(files) ? files : [];
    const currentIndex = validFiles.findIndex(f => f.url === file.url || f.id === file.id);
    const modalRef = this.modalService.open(PreviewPopupComponent, {
      size: "lg",
      backdrop: "static",
      keyboard: false,
      centered: true,
      windowClass: "preview-modal",
    });
    modalRef.componentInstance.file = { ...file, fileSize: this.getFileSize(file) };
    modalRef.componentInstance.type = previewType;
    modalRef.componentInstance.brand = this.brand;
    modalRef.componentInstance.files = validFiles;
    modalRef.componentInstance.currentIndex = currentIndex >= 0 ? currentIndex : 0;
  }

  private canOpenFile(file: any): boolean {
    if (!file.url) return false;

    // Check if file extension can be opened in browser
    const url = file.url.toLowerCase();
    const fileName = file.fileName ? file.fileName.toLowerCase() : "";

    // Common file types that can be opened in browser
    const browserSupportedExtensions = [
      ".pdf",
      ".txt",
      ".html",
      ".htm",
      ".xml",
      ".json",
      ".csv",
      ".jpg",
      ".jpeg",
      ".png",
      ".gif",
      ".bmp",
      ".svg",
      ".webp",
      ".mp4",
      ".webm",
      ".ogg",
      ".mp3",
      ".wav",
      ".aac",
    ];

    // Check if URL contains supported extension
    for (const ext of browserSupportedExtensions) {
      if (url.includes(ext) || fileName.includes(ext)) {
        return true;
      }
    }

    // If no extension found, assume it can be opened (server will handle content-type)
    return true;
  }

  onDownloadClick(file: any): void {
    // Use downloadSingleFile now that we removed proxy
    this.downloadSingleFile(file);
    // this.directDownload(file);
  }

  // Direct download method to bypass CORS
  private directDownload(file: any): void {
    if (!file.url) {
      this.toastr.error("Download URL not available", "Error");
      return;
    }
    const filename = file.fileName || file.url.split("/").pop() || "download";

    try {
      // Method 1: Try with download attribute
      const a = document.createElement("a");
      a.href = file.url;
      a.download = filename;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      this.toastr.success(`Download started: ${filename}`, "Success");
    } catch (error) {
      // Method 2: Try opening in new tab
      try {
        window.open(file.url, "_blank");
        this.toastr.info(`Opened ${filename} in new tab`, "Info");
      } catch (tabError) {
        this.toastr.error(`Failed to download: ${filename}`, "Error");
      }
    }
  }

  // Simple download method for testing
  private simpleDownload(file: any): void {
    if (!file.url) {
      this.toastr.error("Download URL not available", "Error");
      return;
    }

    const filename = file.fileName || file.url.split("/").pop() || "download";

    // Test with fetch first
    fetch(file.url)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.blob();
      })
      .then(blob => {
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        this.toastr.success(`Download started: ${filename}`, "Success");
      })
      .catch(error => {
        this.toastr.error(`Download failed: ${error.message}`, "Error");

        // Fallback to direct link
        const a = document.createElement("a");
        a.href = file.url;
        a.download = filename;
        a.target = "_blank";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        this.toastr.info(`Trying direct link for: ${filename}`, "Info");
      });
  }

  getTotalFileSize(): string {
    const validMedia = this.validMedia;
    if (!validMedia || validMedia.length === 0) {
      return "0 B";
    }

    // Calculate total size in bytes
    const totalBytes = validMedia.reduce((total, file) => {
      try {
        const fileSize = this.parseFileSize(file.size || "0 B");
        return total + fileSize;
      } catch (error) {
        console.warn("Error parsing file size:", { file, error });
        return total;
      }
    }, 0);

    return this.formatFileSize(totalBytes);
  }

  private parseFileSize(sizeString: string | number | undefined | null | any): number {
    // Handle null, undefined, or falsy values
    if (!sizeString && sizeString !== 0) return 0;

    // If sizeString is already a number, return it directly (assuming it's in bytes)
    if (typeof sizeString === "number") {
      return isNaN(sizeString) ? 0 : sizeString;
    }

    // If it's not a string, try to convert it
    if (typeof sizeString !== "string") {
      // Try to convert to string first
      try {
        sizeString = String(sizeString);
      } catch (error) {
        console.warn("Cannot convert sizeString to string:", sizeString);
        return 0;
      }
    }

    // Now it should be a string, parse it
    try {
      const size = sizeString.toLowerCase().trim();
      const number = parseFloat(size.replace(/[^\d.]/g, ""));
      const unit = size.replace(/[\d.\s]/g, "");

      switch (unit) {
        case "b":
          return number;
        case "kb":
          return number * 1024;
        case "mb":
          return number * 1024 * 1024;
        case "gb":
          return number * 1024 * 1024 * 1024;
        case "tb":
          return number * 1024 * 1024 * 1024 * 1024;
        default:
          // If no unit specified, assume bytes
          return number;
      }
    } catch (error) {
      console.warn("Error parsing file size string:", { sizeString, error });
      return 0;
    }
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  getRemainingTime(): string {
    if (!this.subSection || !this.subSection.createdAt) {
      return "0 months 0 days";
    }
    // Calculate expiry date: createdAt + 1 year
    const createdAt = new Date(this.subSection.createdAt);
    const expiryDate = new Date(createdAt);
    expiryDate.setFullYear(expiryDate.getFullYear() + 1);
    const now = new Date();
    const diffTime = expiryDate.getTime() - now.getTime();

    if (diffTime <= 0) {
      return "0 months 0 days";
    }

    // Calculate total days
    const totalDays = diffTime / (1000 * 60 * 60 * 24);

    function getDaysInYear(year) {
      const start = new Date(year, 0, 1); // 1 Jan
      const end = new Date(year + 1, 0, 1); // 1 Jan năm sau
      const diffMs = end.getTime() - start.getTime();
      return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    }
    // Check if totalDays >= 365 (1 year)
    if (totalDays >= Math.max(getDaysInYear(createdAt.getFullYear()), getDaysInYear(expiryDate.getFullYear()))) {
      return "1 year";
    }

    // Calculate months and days more accurately
    const tempDate = new Date(now);
    let months = 0;

    // Calculate months by adding months one by one
    while (tempDate < expiryDate) {
      tempDate.setMonth(tempDate.getMonth() + 1);
      if (tempDate <= expiryDate) {
        months++;
      } else {
        tempDate.setMonth(tempDate.getMonth() - 1);
        break;
      }
    }

    // Calculate remaining days
    const remainingTime = expiryDate.getTime() - tempDate.getTime();
    const days = Math.floor(remainingTime / (1000 * 60 * 60 * 24));

    // Format output based on conditions
    if (months === 0) {
      // Less than 1 month: display days only
      return `${days} days`;
    } else if (days === 0) {
      // Exact months: display months only
      return `${months} months`;
    } else {
      // Months and days: display both
      return `${months} months ${days} days`;
    }
  }

  getFileIcon(): string {
    switch (this.type) {
      case "photo":
        return "assets/icons/li_camera.svg";
      case "video":
        return "assets/icons/li_video.svg";
      case "media":
        return "assets/icons/li_folder-open.svg";
      default:
        return "assets/icons/li_file.svg";
    }
  }

  getFileCountText(): string {
    const count = this.validMedia.length;

    switch (this.type) {
      case "photo":
        return count === 1 ? "1 photo" : `${count} photos`;
      case "video":
        return count === 1 ? "1 video" : `${count} videos`;
      case "media":
        return count === 1 ? "1 media item" : `${count} media items`;
      default:
        return count === 1 ? "1 file" : `${count} files`;
    }
  }

  /**
   * For type `media`: breakdown of valid items into images, videos, and other files (e.g. PDF).
   * Video takes precedence if both image and video heuristics match.
   */
  getMediaStatLine(): string {
    let images = 0;
    let videos = 0;
    let others = 0;
    for (const file of this.validMedia) {
      if (this.isMediaVideo(file)) {
        videos++;
      } else if (this.isMediaPhoto(file)) {
        images++;
      } else {
        others++;
      }
    }
    const parts: string[] = [];
    parts.push(images === 1 ? "1 image" : `${images} images`);
    parts.push(videos === 1 ? "1 video" : `${videos} videos`);
    if (others > 0) {
      parts.push(others === 1 ? "1 other file" : `${others} other files`);
    }
    return parts.join(" · ");
  }

  getFileSize(file: any): string {
    if (!file.size || Number(file.size) <= 0) {
      return "Unknown size";
    }
    return this.formatFileSize(file.size);
  }

  /**
   * Get valid media items (filter out items with invalid/broken URLs)
   */
  get validMedia(): any[] {
    if (!this.subSection || !this.subSection.media) {
      return [];
    }
    return this.subSection.media.filter((file: any) => {
      return this.isUrlValid(file.url);
    });
  }

  /** Chunk validMedia into rows for virtual scroll grid - only visible rows render in DOM */
  get mediaRows(): any[][] {
    const media = this.validMedia;
    const rows: any[][] = [];
    for (let i = 0; i < media.length; i += GRID_COLUMNS) {
      rows.push(media.slice(i, i + GRID_COLUMNS));
    }
    return rows;
  }

  /**
   * Handle image load error - mark URL as invalid
   * This catches errors when S3 file doesn't exist (404, 403, etc.)
   */
  onImageError(event: any, file: any): void {
    if (file && file.url) {
      this.markUrlAsInvalid(file.url);
    }
  }

  /**
   * Handle video load error - mark URL as invalid
   * This catches errors when S3 file doesn't exist (404, 403, etc.)
   */
  onVideoError(event: any, file: any): void {
    if (file && file.url) {
      this.markUrlAsInvalid(file.url);
    }
  }

  /**
   * Mark URL as invalid and update UI
   * This is a common method used by all error handlers
   */
  private markUrlAsInvalid(url: string): void {
    if (!url || url.trim() === "") {
      return;
    }

    // Add to invalid URLs set
    this.invalidMediaUrls.add(url);

    // Force change detection to update the view immediately
    if (this.subSection && this.subSection.media) {
      this.subSection.media = [...this.subSection.media];
    }

    // Update isNoFiles flag
    this.updateNoFilesFlag();
    this.cdr.markForCheck();
  }

  /**
   * Check if URL is valid (not marked as invalid)
   * Use this for quick synchronous checks before operations
   */
  isUrlValid(url: string): boolean {
    if (!url || url.trim() === "") {
      return false;
    }
    return !this.invalidMediaUrls.has(url);
  }

  /**
   * Validate URL by trying to fetch first bytes
   * This is the main validation method - more reliable than HEAD request for CORS
   * Returns true if URL is valid, false if invalid
   */
  private async validateUrl(url: string): Promise<boolean> {
    // Quick check first
    if (!this.isUrlValid(url)) {
      return false;
    }

    if (!url || url.trim() === "") {
      this.markUrlAsInvalid(url);
      return false;
    }

    try {
      // Try to fetch first 1 byte to check if file exists
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Range: "bytes=0-0", // Request only first byte
        },
      });

      // Check if response is ok (200 or 206 for partial content)
      if (response.ok || response.status === 206) {
        return true;
      }

      // If status is 404, 403, etc., file doesn't exist or is not accessible
      if (response.status === 404 || response.status === 403 || response.status >= 400) {
        this.markUrlAsInvalid(url);
        return false;
      }

      return true;
    } catch (error) {
      // If fetch fails (network error, CORS, etc.), mark as invalid
      this.markUrlAsInvalid(url);
      return false;
    }
  }

  /**
   * Update isNoFiles flag based on valid media count
   */
  private updateNoFilesFlag(): void {
    const validMediaCount = this.validMedia.length;
    this.isNoFiles = validMediaCount === 0;
  }

  /**
   * Handle file validation error - mark URL as invalid
   * This is used for file type (PDF, etc.) when validation fails
   */
  onFileError(file: any): void {
    if (file && file.url) {
      this.markUrlAsInvalid(file.url);
    }
  }

  /**
   * Validate all media URLs when component loads (for all types: file, photo, video)
   * This validates URLs to ensure only valid ones are shown in UI
   *  * Note: Skip pre-validation for photos - S3/CORS may block Range requests, causing false negatives.
   * Photos are shown immediately; onImageError handles load failures.
   */
  private async validateMediaUrls(): Promise<void> {
    if (!this.subSection || !this.subSection.media) {
      return;
    }

    return;
  }

  trackByFile(index: number, file: any): string | number {
    // Use a unique identifier: prefer id if available, otherwise use index
    return file.id !== null && file.id !== undefined ? file.id : `file-${index}`;
  }

  /** trackBy for virtual scroll rows - avoids unnecessary re-renders when scrolling */
  trackByFileRow(index: number, row: any[]): string {
    const first = row[0];
    const firstId = first ? (first.id != null ? first.id : "file-" + index * GRID_COLUMNS) : "row-" + index;
    return "row-" + index + "-" + firstId;
  }

  /** Global index of file in validMedia for selection logic */
  getFileIndexInMedia(rowIndex: number, colIndex: number): number {
    return rowIndex * GRID_COLUMNS + colIndex;
  }

  /** Viewport height for virtual scroll - ensures smooth scrolling for 1000+ items */
  getViewportHeight(): number {
    if (typeof window === "undefined") return 500;
    return Math.min(700, Math.max(400, window.innerHeight - 350));
  }

  /** Prefer thumbnail for card display - smaller file, faster load */
  getPhotoDisplayUrl(file: any): string {
    return (file && (file.thumbnail || file.url)) || "";
  }

  isMediaPhoto(file: any): boolean {
    if (!file) return false;
    const mediaType = (file.type || "").toString().toLowerCase().trim();
    if (mediaType.startsWith("image") || mediaType === "photo") {
      return true;
    }
    const fileName = ((file.fileName || "") as string).toLowerCase().trim();
    const urlPath = ((file.url || "") as string).toLowerCase().trim().split("?")[0].split("#")[0];
    return /\.(jpg|jpeg|png|gif|bmp|webp|svg|heic|heif)$/.test(fileName || urlPath);
  }

  isMediaVideo(file: any): boolean {
    if (!file) return false;
    const mediaType = (file.type || "").toString().toLowerCase().trim();
    if (mediaType.startsWith("video")) {
      return true;
    }
    const fileName = ((file.fileName || "") as string).toLowerCase().trim();
    const urlPath = ((file.url || "") as string).toLowerCase().trim().split("?")[0].split("#")[0];
    return /\.(mp4|webm|ogg|mov|avi|wmv|mkv|m4v)$/.test(fileName || urlPath);
  }

  /** Avoid blank white video cards when thumbnail is not ready yet. */
  getVideoPoster(file: any): string {
    if (file && typeof file.thumbnail === "string" && file.thumbnail.trim() !== "") {
      return file.thumbnail;
    }
    return this.defaultVideoPoster;
  }

  getFileIdentifier(file: any, index: number): string | number {
    // Prefer id, then url (stable across validMedia vs subSection.media), fallback to index
    if (file.id != null && file.id !== "") return file.id;
    if (file.url) return file.url;
    return "file-" + index;
  }

  toggleFileSelection(file: any, index: number, event: Event): void {
    event.stopPropagation();
    const fileId = this.getFileIdentifier(file, index);

    if (this.selectedFiles.has(fileId)) {
      this.selectedFiles.delete(fileId);
    } else {
      this.selectedFiles.add(fileId);
    }
    this.selectedCount = this.selectedFiles.size;
  }

  isFileSelected(file: any, index: number): boolean {
    const fileId = this.getFileIdentifier(file, index);
    return this.selectedFiles.has(fileId);
  }

  selectAllFiles(): void {
    if (this.subSection && this.subSection.media) {
      if (this.selectedFiles.size === this.subSection.media.length) {
        // If all files are selected, deselect all
        this.selectedFiles.clear();
      } else {
        // Select all files
        this.subSection.media.forEach((file, index) => {
          const fileId = this.getFileIdentifier(file, index);
          this.selectedFiles.add(fileId);
        });
      }
      this.selectedCount = this.selectedFiles.size;
    }
  }

  downloadSelectedFiles(): void {
    if (this.subSection && this.subSection.media) {
      const selectedFiles = this.subSection.media.filter((file, index) => {
        const fileId = this.getFileIdentifier(file, index);
        return this.selectedFiles.has(fileId);
      });
      console.log("Selected files:", selectedFiles);

      if (selectedFiles.length > 0) {
        if (selectedFiles.length > 1) {
          // Download multiple files as zip
          this.downloadWithProgress(selectedFiles, true, false); // true = isZip, false = isDownloadAll
        } else {
          // Download single file directly
          this.downloadSingleFile(selectedFiles[0]);
        }
      }
    }
  }

  // Download progress helper methods
  private initializeDownloadProgress(totalFiles: number): void {
    this.downloadCancelled = false;
    this.downloadProgress = {
      current: 0,
      total: totalFiles,
      percentage: 0,
      currentFile: "Preparing download...",
      status: "downloading",
      message: "",
    };
    this.showDownloadProgress = true;
    this.cdr.markForCheck();

    // // Force change detection to ensure UI updates
    setTimeout(() => {
      this.updateDownloadProgress(0, totalFiles, "Starting download...");
    }, 0);
  }

  private updateDownloadProgress(current: number, total: number, currentFile: string, percent?: number): void {
    // Calculate percentage correctly
    let percentage = 0;
    if (total === 1) {
      // For single file, show progress in stages: 0% -> 100%
      percentage = Math.round((current / total) * 100);
    } else {
      // For multiple files, show progress based on completed files
      percentage = total > 0 ? Math.min(Math.round((current / total) * 100), 100) : 0;
    }

    if (percent) percentage = percent;

    // Create a new object to trigger change detection
    this.downloadProgress = {
      ...this.downloadProgress,
      current: current,
      total: total,
      percentage: percentage,
      currentFile: currentFile,
      status: "downloading",
    };
    this.cdr.markForCheck();
  }

  private updateSingleFileProgress(
    stage: "preparing" | "downloading" | "completed",
    filename: string,
    percent?: number
  ): void {
    let percentage = 0;
    let current = 0;
    let status: "preparing" | "downloading" | "completed" = "preparing";

    switch (stage) {
      case "preparing":
        percentage = 0;
        current = 0;
        status = "downloading";
        break;
      case "downloading":
        percentage = percent || 0;
        current = 0;
        status = "downloading";
        break;
      case "completed":
        percentage = 100;
        current = 1;
        status = "completed";
        break;
    }

    this.downloadProgress = {
      ...this.downloadProgress,
      current,
      total: 1,
      percentage,
      currentFile: filename,
      status, // ✅ giữ đúng status
    };
    this.cdr.markForCheck();
  }

  private completeDownload(message: string): void {
    // Create a new object to trigger change detection
    this.downloadProgress = {
      ...this.downloadProgress,
      status: "completed",
      message: message,
      percentage: 100,
    };
    this.cdr.markForCheck();

    // Auto-hide after 2 seconds and clean up
    setTimeout(() => {
      this.showDownloadProgress = false;
      this.downloadProgress = {
        ...this.downloadProgress,
        status: "idle",
      };

      // Clean up AbortController after completion
      if (this.downloadAbortController) {
        this.downloadAbortController = null;
      }
      this.cdr.markForCheck();
    }, 2000);
  }

  private errorDownload(message: string): void {
    // Create a new object to trigger change detection
    this.downloadProgress = {
      ...this.downloadProgress,
      status: "error",
      message: message,
    };
    this.cdr.markForCheck();

    // Auto-hide after 5 seconds and clean up
    setTimeout(() => {
      this.showDownloadProgress = false;
      this.downloadProgress = {
        ...this.downloadProgress,
        status: "idle",
      };

      // Clean up AbortController after error
      if (this.downloadAbortController) {
        this.downloadAbortController = null;
      }
      this.cdr.markForCheck();
    }, 5000);
  }

  private cancelDownload(): void {
    this.showDownloadProgress = false;
    this.downloadProgress = {
      ...this.downloadProgress,
      status: "idle",
    };
    this.downloadCancelled = false;

    // Clean up AbortController
    if (this.downloadAbortController) {
      this.downloadAbortController = null;
    }

    this.loadingService.complete(); // Complete loading service
  }

  private downloadWithProgress(files: any[], isZip: boolean = false, isDownloadAll: boolean = true): void {
    this.loadingService.start();
    if (isZip) {
      this.downloadAsZip(files, isDownloadAll);
    } else if (files.length === 1) {
      this.downloadSingleFile(files[0]);
    } else {
      // Multiple files as individual downloads
      this.downloadMultipleFiles(files);
    }
  }

  private getDownloadBlobPromise(file: any, signal: AbortSignal): Promise<Blob> {
    if (this.type === "photo" || (this.type === "media" && this.isMediaPhoto(file))) {
      return this.downloadService.fetchImageAsBlob(file.url, signal);
    }
    return this.downloadService.fetchFileAsBlob(file.url, signal);
  }

  private downloadMultipleFiles(files: any[]): void {
    this.initializeDownloadProgress(files.length);
    this.downloadAbortController = new AbortController();
    let completedCount = 0;
    let successCount = 0;
    let errorCount = 0;
    const totalFiles = files.length;

    const downloadPromises = files.map((file, index) => {
      const filename = file.fileName || file.url.split("/").pop() || `file_${index + 1}`;

      // Use appropriate method based on file type with abort signal
      const fetchMethod = this.getDownloadBlobPromise(file, this.downloadAbortController.signal);

      return fetchMethod
        .then(blob => {
          if (this.downloadCancelled || this.downloadAbortController.signal.aborted) {
            throw new Error("Download cancelled");
          }

          const downloadUrl = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = downloadUrl;
          a.download = filename;
          a.click();
          window.URL.revokeObjectURL(downloadUrl);

          completedCount++;
          successCount++;

          // Force change detection
          setTimeout(() => {
            this.updateDownloadProgress(completedCount, totalFiles, `Downloaded ${filename}`);

            if (completedCount === totalFiles) {
              if (errorCount === 0) {
                this.completeDownload(`Downloaded ${totalFiles} files successfully`);
                this.toastr.success(`Downloaded ${totalFiles} files successfully`, "Success");
              } else {
                this.completeDownload(`Downloaded ${successCount} files, ${errorCount} failed`);
                this.toastr.warning(`Downloaded ${successCount} files, ${errorCount} failed`, "Warning");
              }
              this.loadingService.complete(); // Complete loading service
            }
          }, 0);
        })
        .catch(error => {
          if (this.downloadCancelled || this.downloadAbortController.signal.aborted || error.name === "AbortError") {
            throw new Error("Download cancelled");
          }

          completedCount++;
          errorCount++;

          // Force change detection
          setTimeout(() => {
            this.updateDownloadProgress(completedCount, totalFiles, `Failed to download ${filename}`);

            if (completedCount === totalFiles) {
              if (successCount === 0) {
                this.errorDownload("All files failed to download");
                this.toastr.error("All files failed to download", "Error");
              } else {
                this.completeDownload(`Downloaded ${successCount} files, ${errorCount} failed`);
                this.toastr.warning(`Downloaded ${successCount} files, ${errorCount} failed`, "Warning");
              }
              this.loadingService.complete(); // Complete loading service
            }
          }, 0);
        });
    });

    Promise.all(downloadPromises).catch(error => {
      if (error.message === "Download cancelled" || error.name === "AbortError") {
        this.cancelDownload();
      } else {
        this.errorDownload("Download failed");
      }
      this.loadingService.complete(); // Complete loading service
    });
  }

  private downloadSingleFile(file: any): void {
    if (!file.url) {
      this.toastr.error("Download URL not available", "Error");
      return;
    }

    // Check if URL is already marked as invalid
    if (!this.isUrlValid(file.url)) {
      this.toastr.error("File URL is invalid or not accessible", "Error");
      return;
    }

    // reset progress trước khi bắt đầu
    this.updateSingleFileProgress("preparing", file.fileName, 0);

    this.loadingService.start();
    this.downloadAbortController = new AbortController();
    this.downloadCancelled = false;

    const filename = file.fileName || file.url.split("/").pop() || "download";
    this.initializeDownloadProgress(1);

    this.downloadWithRetry(file)
      .then(() => {
        this.completeDownload("File downloaded successfully");
        this.updateSingleFileProgress("completed", `Downloaded ${filename}`);
        this.toastr.success(`Started downloading: ${filename}`, "Success");
        this.loadingService.complete();
      })
      .catch(error => {
        if (error.message === "Download cancelled" || error.name === "AbortError") {
          this.cancelDownload();
        } else {
          // Check if error is due to file not found (404, 403, etc.)
          if (
            error.status === 404 ||
            error.status === 403 ||
            (error.message &&
              (error.message.includes("404") || error.message.includes("403") || error.message.includes("not found")))
          ) {
            // Mark file as invalid if it doesn't exist
            this.markUrlAsInvalid(file.url);
            this.toastr.error(`File not found or not accessible: ${filename}`, "Error");
          } else {
            this.errorDownload("Failed to download file");
          }
        }
        this.loadingService.complete();
      });
  }

  private downloadWithRetry(file: any, maxRetries: number = 3): Promise<void> {
    return new Promise((resolve, reject) => {
      let retryCount = 0;

      const attemptDownload = () => {
        if (this.downloadCancelled || this.downloadAbortController.signal.aborted) {
          reject(new Error("Download cancelled"));
          return;
        }

        const filename = file.fileName || file.url.split("/").pop() || "download";
        const fileSize = file.size || 0;
        const fileSizeMB = fileSize / (1024 * 1024);

        // Check if file is large (>100MB) - use Multi-thread Range download
        const useMultiThread = fileSizeMB > 100;

        if (useMultiThread) {
          this.updateSingleFileProgress("preparing", `Preparing large file (${this.formatFileSize(fileSize)})...`);
          this.downloadWithMultiThreadRange(file, filename, fileSize)
            .then(() => {
              this.updateSingleFileProgress("completed", `Downloaded ${filename}`);
              resolve();
            })
            .catch(error => {
              if (
                this.downloadCancelled ||
                this.downloadAbortController.signal.aborted ||
                error.name === "AbortError"
              ) {
                reject(new Error("Download cancelled"));
                return;
              }

              retryCount++;
              if (retryCount < maxRetries) {
                this.updateSingleFileProgress("preparing", `Retrying download... (${retryCount}/${maxRetries})`);
                setTimeout(attemptDownload, 1000 * retryCount);
              } else {
                // Check if error is due to file not found
                const errorWithStatus = error as any;
                if (errorWithStatus.status === 404 || errorWithStatus.status === 403) {
                  reject({ ...error, status: errorWithStatus.status });
                } else {
                  reject(new Error(`Failed to download after ${maxRetries} attempts: ${error.message}`));
                }
              }
            });
        } else {
          // Use optimized method for smaller files
          this.downloadWithOptimizedBlob(file, filename)
            .then(() => {
              this.updateSingleFileProgress("completed", `Downloaded ${filename}`);
              resolve();
            })
            .catch(error => {
              if (
                this.downloadCancelled ||
                this.downloadAbortController.signal.aborted ||
                error.name === "AbortError"
              ) {
                reject(new Error("Download cancelled"));
                return;
              }

              retryCount++;
              if (retryCount < maxRetries) {
                this.updateSingleFileProgress("preparing", `Retrying download... (${retryCount}/${maxRetries})`);
                setTimeout(attemptDownload, 1000 * retryCount);
              } else {
                // Check if error is due to file not found
                const errorWithStatus = error as any;
                if (errorWithStatus.status === 404 || errorWithStatus.status === 403) {
                  reject({ ...error, status: errorWithStatus.status });
                } else {
                  reject(new Error(`Failed to download after ${maxRetries} attempts: ${error.message}`));
                }
              }
            });
        }
      };

      attemptDownload();
    });
  }

  // ✅ NEW: Download với Multi-thread Range (cho file lớn >100MB)
  private async downloadWithMultiThreadRange(file: any, filename: string, fileSize: number): Promise<void> {
    const encodeImgUrl = (url: string): string => {
      const domain = url.slice(0, url.indexOf("/", 8) + 1);
      const folder = url.slice(url.indexOf("/", 8) + 1);
      const encode = folder.includes("/") ? encodeURIComponent(folder) : folder;
      return domain + encode;
    };

    const encodedUrl = encodeImgUrl(file.url);

    // First, get file size by HEAD request or use provided size
    let contentLength = fileSize;
    try {
      const headResponse = await fetch(encodedUrl, {
        method: "HEAD",
        signal: this.downloadAbortController.signal,
      });

      // Check if file exists
      if (headResponse.status === 404 || headResponse.status === 403) {
        const error: any = new Error(`File not found: ${headResponse.statusText}`);
        error.status = headResponse.status;
        throw error;
      }

      if (headResponse.ok) {
        const contentLengthHeader = headResponse.headers.get("Content-Length");
        if (contentLengthHeader) {
          contentLength = parseInt(contentLengthHeader, 10);
        }
        // Check if server supports Range requests
        const acceptRanges = headResponse.headers.get("Accept-Ranges");
        if (acceptRanges !== "bytes" && acceptRanges !== "none") {
          console.warn("⚠️ Server may not support Range requests, falling back to single-thread download");
          // Fallback to single-thread if Range not supported
          return this.downloadWithStreamSaver(file, filename, fileSize);
        }
      }
    } catch (error) {
      // If error is 404/403, re-throw it with status
      const errorWithStatus = error as any;
      if (errorWithStatus.status === 404 || errorWithStatus.status === 403) {
        throw error;
      }
      console.warn("⚠️ Could not check Range support, proceeding with multi-thread:", error);
    }

    // ✅ OPTIMIZED: Tăng số threads và giảm chunk size để tăng tốc độ download
    const MIN_CHUNK_SIZE = 5 * 1024 * 1024; // 5MB minimum per chunk (giảm từ 10MB)
    const MAX_THREADS = 20; // Tăng từ 8 lên 20 để match với upload và tăng tốc độ
    const OPTIMAL_CHUNK_SIZE = 10 * 1024 * 1024; // 10MB per chunk (giảm từ 50MB để có nhiều chunks hơn)

    // Tính toán chunk size để có nhiều chunks hơn và tận dụng tối đa threads
    const chunkSize = Math.max(MIN_CHUNK_SIZE, Math.min(OPTIMAL_CHUNK_SIZE, Math.ceil(contentLength / MAX_THREADS)));
    const totalChunks = Math.ceil(contentLength / chunkSize);
    const actualThreads = Math.min(MAX_THREADS, totalChunks);

    console.log(`📥 Multi-thread download: ${this.formatFileSize(contentLength)}`, {
      chunkSize: this.formatFileSize(chunkSize),
      totalChunks,
      threads: actualThreads,
    });

    // Create ranges for each chunk
    const ranges: Array<{ start: number; end: number; index: number }> = [];
    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize - 1, contentLength - 1);
      ranges.push({ start, end, index: i });
    }

    // Download chunks map: index -> Uint8Array
    const chunksMap = new Map<number, Uint8Array>();
    let completedChunks = 0;
    let totalReceived = 0;
    let lastProgressUpdate = 0;
    let lastProgressTime = Date.now();
    const PROGRESS_UPDATE_INTERVAL = 500; // Update mỗi 500ms (tăng tần suất để UI mượt hơn)

    // Download function for a single range
    const downloadRange = async (range: { start: number; end: number; index: number }): Promise<void> => {
      if (this.downloadCancelled || this.downloadAbortController.signal.aborted) {
        throw new Error("Download cancelled");
      }

      const rangeHeader = `bytes=${range.start}-${range.end}`;
      const response = await fetch(encodedUrl, {
        headers: {
          Range: rangeHeader,
        },
        signal: this.downloadAbortController.signal,
      });

      if (!response.ok) {
        // If Range not supported (206 Partial Content expected), fallback
        if (response.status === 200) {
          throw new Error("Server does not support Range requests");
        }
        throw new Error(`Failed to fetch range ${range.start}-${range.end}: ${response.statusText}`);
      }

      // Verify we got partial content
      if (response.status !== 206) {
        throw new Error(`Expected 206 Partial Content, got ${response.status}`);
      }

      const reader = response.body.getReader();
      const chunks: Uint8Array[] = [];
      let received = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        if (value) {
          chunks.push(value);
          received += value.length;
        }
      }

      // Combine chunks for this range
      const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
      const combined = new Uint8Array(totalLength);
      let offset = 0;
      for (const chunk of chunks) {
        combined.set(chunk, offset);
        offset += chunk.length;
      }

      chunksMap.set(range.index, combined);
      completedChunks++;
      totalReceived += received;

      // Update progress
      const now = Date.now();
      if (now - lastProgressTime >= PROGRESS_UPDATE_INTERVAL || completedChunks === totalChunks) {
        const percent = contentLength > 0 ? Math.round((totalReceived / contentLength) * 100) : 0;
        const receivedMB = (totalReceived / (1024 * 1024)).toFixed(2);
        const totalMB = (contentLength / (1024 * 1024)).toFixed(2);

        this.updateSingleFileProgress(
          "downloading",
          `Downloading ${filename}... ${percent}% (${receivedMB}MB / ${totalMB}MB) [${completedChunks}/${totalChunks} chunks]`,
          percent
        );

        lastProgressTime = now;
      }
    };

    // Download chunks in parallel (with concurrency limit)
    const downloadQueue = [...ranges];
    const activeDownloads = new Set<Promise<void>>();
    const MAX_CONCURRENT = actualThreads;

    const processQueue = async (): Promise<void> => {
      while (downloadQueue.length > 0 || activeDownloads.size > 0) {
        // Start new downloads up to concurrency limit
        while (activeDownloads.size < MAX_CONCURRENT && downloadQueue.length > 0) {
          const range = downloadQueue.shift();
          if (!range) break;

          const downloadPromise = downloadRange(range)
            .then(() => {
              activeDownloads.delete(downloadPromise);
            })
            .catch(error => {
              activeDownloads.delete(downloadPromise);
              // If Range not supported, stop all and fallback
              if (error.message && error.message.includes("does not support Range")) {
                downloadQueue.length = 0;
                activeDownloads.forEach(p => {
                  p.catch(() => {}); // Ignore errors when cancelling
                });
                throw error;
              }
              throw error;
            });

          activeDownloads.add(downloadPromise);
        }

        // Wait for at least one download to complete
        if (activeDownloads.size > 0) {
          await Promise.race(Array.from(activeDownloads));
        }
      }
    };

    try {
      await processQueue();

      // Verify all chunks are downloaded
      if (chunksMap.size !== totalChunks) {
        throw new Error(`Missing chunks: expected ${totalChunks}, got ${chunksMap.size}`);
      }

      // ✅ OPTIMIZED: Stream chunks directly to disk as soon as they're ready
      console.log(`🔗 Writing ${totalChunks} chunks to disk...`);
      this.updateSingleFileProgress("downloading", `Writing chunks to disk... (${totalChunks} chunks)`);

      // Use StreamSaver to write combined chunks to disk
      const fileStream = streamSaver.createWriteStream(filename, {
        size: contentLength,
      });

      const writer = fileStream.getWriter();

      // Write chunks in order and free memory immediately
      for (let i = 0; i < totalChunks; i++) {
        const chunk = chunksMap.get(i);
        if (!chunk) {
          throw new Error(`Missing chunk ${i}`);
        }
        await writer.write(chunk);
        // Clear chunk from memory immediately after writing to save memory
        chunksMap.delete(i);

        // Update progress during writing
        if (i % Math.max(1, Math.floor(totalChunks / 10)) === 0 || i === totalChunks - 1) {
          const writePercent = Math.round(((i + 1) / totalChunks) * 100);
          this.updateSingleFileProgress("downloading", `Writing to disk... ${writePercent}%`);
        }
      }

      await writer.close();
      console.log(`✅ Multi-thread download complete: ${this.formatFileSize(totalReceived)}`);
    } catch (error) {
      // If Range not supported, fallback to single-thread
      if (error.message && error.message.includes("does not support Range")) {
        console.log("⚠️ Falling back to single-thread download");
        return this.downloadWithStreamSaver(file, filename, fileSize);
      }
      throw error;
    }
  }

  // ✅ FALLBACK: Download với StreamSaver (single-thread, cho file lớn >100MB)
  private async downloadWithStreamSaver(file: any, filename: string, fileSize: number): Promise<void> {
    const encodeImgUrl = (url: string): string => {
      const domain = url.slice(0, url.indexOf("/", 8) + 1);
      const folder = url.slice(url.indexOf("/", 8) + 1);
      const encode = folder.includes("/") ? encodeURIComponent(folder) : folder;
      return domain + encode;
    };

    const encodedUrl = encodeImgUrl(file.url);
    const response = await fetch(encodedUrl, {
      signal: this.downloadAbortController.signal,
    });

    if (!response.ok || !response.body) {
      throw new Error(`Failed to fetch: ${response.statusText}`);
    }

    const contentLength = parseInt(response.headers.get("Content-Length") || String(fileSize), 10);

    // ✅ Stream trực tiếp vào disk - không load vào memory
    const fileStream = streamSaver.createWriteStream(filename, {
      size: contentLength,
    });

    const writer = fileStream.getWriter();
    const reader = response.body.getReader();

    let received = 0;
    let lastProgressUpdate = 0;
    let lastProgressTime = Date.now();

    // ✅ Optimized progress updates for large files
    const PROGRESS_UPDATE_INTERVAL = 2000; // Update mỗi 2 giây
    const CHUNK_SIZE_THRESHOLD = Math.max(contentLength * 0.01, 10 * 1024 * 1024); // 1% hoặc 10MB

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        if (value) {
          await writer.write(value);
          received += value.length;

          // ✅ Throttle progress updates
          const now = Date.now();
          const timeSinceLastUpdate = now - lastProgressTime;
          const bytesSinceLastUpdate = received - lastProgressUpdate;

          if (
            bytesSinceLastUpdate >= CHUNK_SIZE_THRESHOLD ||
            timeSinceLastUpdate >= PROGRESS_UPDATE_INTERVAL ||
            received === contentLength
          ) {
            const percent = contentLength > 0 ? Math.round((received / contentLength) * 100) : 0;
            const receivedMB = (received / (1024 * 1024)).toFixed(2);
            const totalMB = (contentLength / (1024 * 1024)).toFixed(2);

            this.updateSingleFileProgress(
              "downloading",
              `Downloading ${filename}... ${percent}% (${receivedMB}MB / ${totalMB}MB)`,
              percent
            );

            lastProgressUpdate = received;
            lastProgressTime = now;
          }
        }
      }

      await writer.close();
      console.log(`✅ Stream download complete: ${this.formatFileSize(received)}`);
    } catch (error) {
      try {
        await writer.abort();
      } catch (abortError) {
        // Ignore abort errors
      }
      throw error;
    }
  }

  // ✅ OPTIMIZED: Download với Blob (cho file nhỏ <100MB)
  private async downloadWithOptimizedBlob(file: any, filename: string): Promise<void> {
    const encodeImgUrl = (url: string): string => {
      const domain = url.slice(0, url.indexOf("/", 8) + 1);
      const folder = url.slice(url.indexOf("/", 8) + 1);
      const encode = folder.includes("/") ? encodeURIComponent(folder) : folder;
      return domain + encode;
    };

    const encodedUrl = encodeImgUrl(file.url);
    const response = await fetch(encodedUrl, {
      signal: this.downloadAbortController.signal,
    });

    if (!response.ok || !response.body) {
      // Include status code in error for proper handling
      const error: any = new Error(`Failed to fetch: ${response.statusText}`);
      error.status = response.status;
      throw error;
    }

    const contentLength = parseInt(response.headers.get("Content-Length") || "0", 10);
    const reader = response.body.getReader();
    const chunks: Uint8Array[] = [];

    let received = 0;
    let lastProgressUpdate = 0;
    let lastProgressTime = Date.now();

    // ✅ Optimized progress updates
    const PROGRESS_UPDATE_INTERVAL = 500; // 500ms
    const CHUNK_SIZE_THRESHOLD = Math.max(contentLength * 0.005, 1024 * 1024); // 0.5% hoặc 1MB

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      if (value) {
        chunks.push(value);
        received += value.length;

        const now = Date.now();
        const timeSinceLastUpdate = now - lastProgressTime;
        const bytesSinceLastUpdate = received - lastProgressUpdate;

        if (
          contentLength > 0 &&
          (bytesSinceLastUpdate >= CHUNK_SIZE_THRESHOLD ||
            timeSinceLastUpdate >= PROGRESS_UPDATE_INTERVAL ||
            received === contentLength)
        ) {
          const percent = Math.round((received / contentLength) * 100);
          this.updateSingleFileProgress("downloading", `Downloading ${filename}... (${percent}%)`, percent);
          lastProgressUpdate = received;
          lastProgressTime = now;
        }
      }
    }

    if (contentLength > 0) {
      this.updateSingleFileProgress("downloading", `Downloading ${filename}... (100%)`, 100);
    }

    const blob = new Blob(chunks as any, {
      type: response.headers.get("Content-Type") || "application/octet-stream",
    });

    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();

    setTimeout(() => {
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
    }, 100);
  }

  get translationBtnText(): string {
    return AppShareHelper.translationButtonText({
      originalComment: this.isTranslated ? true : null,
    });
  }

  async toggleTranslation(): Promise<void> {
    if (this.translating || !this.subSection) return;

    if (this.isTranslated) {
      this.subSection = JSON.parse(JSON.stringify(this.originalSubSection));
      this.isTranslated = false;
      this.cdr.markForCheck();
      return;
    }

    this.translating = true;
    try {
      this.originalSubSection = JSON.parse(JSON.stringify(this.subSection));

      const entries: Array<{ text: string; apply: (translated: string) => void }> = [];
      const addEntry = (text: string, apply: (t: string) => void) => {
        if (text) entries.push({ text, apply });
      };

      addEntry(this.subSection.title, t => (this.subSection.title = t));
      addEntry(this.subSection.description, t => (this.subSection.description = t));

      if (entries.length === 0) {
        this.toastr.info("No text content to translate.", "Info");
        this.translating = false;
        this.cdr.markForCheck();
        return;
      }

      const targetLang = (navigator.language || "en").split("-")[0];
      await this.translateService.translateFieldEntries(entries, targetLang).toPromise();

      this.isTranslated = true;
    } catch (error) {
      console.error("Translation error:", error);
      if (this.originalSubSection) {
        this.subSection = JSON.parse(JSON.stringify(this.originalSubSection));
      }
      this.toastr.error("Translation failed. Please try again.", "Error");
    } finally {
      this.translating = false;
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    // Revoke any object URLs we created to prevent memory leaks
    this.objectUrlsToRevoke.forEach(url => {
      try {
        URL.revokeObjectURL(url);
      } catch (_) {
        /* ignore */
      }
    });
    this.objectUrlsToRevoke.clear();

    // Clean up navigation info when component is destroyed
    sessionStorage.removeItem("previewNavigationInfo");
    sessionStorage.removeItem("previousUrl");

    // Clean up any ongoing downloads
    if (this.downloadAbortController) {
      this.downloadAbortController.abort();
      this.downloadAbortController = null;
    }
  }
}
